import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import deepmerge from 'deepmerge';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { store } from '../redux';
import { deepmergeArrayIdMerge } from '../utils/deepmerge';
import { useDynamicPaths } from '../hooks/use-dynamic-paths';
import { usePage } from '../hooks/use-page';
export const PageContext = createContext(undefined);
export const PageProvider = ({ children, jaenPage, jaenPages }) => {
    return (_jsxs(PageContext.Provider, { value: {
            jaenPage,
            jaenPages
        }, children: [_jsx("input", { id: "page-file-upload-input", type: "file", style: { display: 'none' } }), children] }));
};
export const usePageContext = () => {
    const context = useContext(PageContext);
    if (context === undefined) {
        throw new Error('useJaenPageContext must be within PageContext');
    }
    return context;
};
export const useJaenPageIndex = (props) => {
    const { jaenPage, jaenPages } = usePageContext();
    const paths = useDynamicPaths({
        staticPages: (jaenPages || [])
    });
    const id = useMemo(() => {
        if (props?.jaenPageId) {
            return props.jaenPageId;
        }
        else if (props?.path) {
            if (jaenPages == null) {
                throw new Error('Unable to resolve page by path. No pages provided.');
            }
            const path = props?.path;
            const newId = paths[path]?.jaenPageId;
            if (!newId) {
                throw new Error(`Could not resolve page by path: ${path}`);
            }
            return newId;
        }
        return jaenPage.id;
    }, [jaenPage, jaenPages, props, paths]);
    const { childPagesOrder = [] } = usePage({ id: id });
    const staticChildren = useMemo(() => {
        let children = [];
        if (jaenPage.id === id) {
            children = jaenPage.childPages || [];
        }
        else {
            children = jaenPages?.find(page => page.id === id)?.childPages || [];
        }
        const uniqueChildren = children.filter((child, index, self) => index === self.findIndex(c => c.id === child.id));
        return uniqueChildren;
    }, [jaenPage, jaenPage.childPages, jaenPages, id]);
    const getDynamicChildren = () => {
        const state = store.getState();
        const page = state.page.pages.nodes[id];
        if (page?.childPages) {
            const actualPages = [];
            for (const { id, deleted } of page.childPages) {
                const actualChild = {
                    ...state.page.pages.nodes[id],
                    id,
                    deleted
                };
                if (actualChild) {
                    actualPages.push(actualChild);
                }
            }
            return actualPages;
        }
        return [];
    };
    const [dynamicChildren, setDynamicChildren] = useState(getDynamicChildren);
    useEffect(() => {
        setDynamicChildren(getDynamicChildren());
        const unsubscribe = store.subscribe(() => {
            setDynamicChildren(getDynamicChildren());
        });
        return () => {
            unsubscribe();
        };
    }, [id]);
    const childPages = useMemo(() => {
        let mergedChildren = deepmerge(staticChildren, dynamicChildren, {
            arrayMerge: deepmergeArrayIdMerge
        });
        mergedChildren = mergedChildren.filter(c => {
            if (props?.includeExcluded) {
                return !c.deleted;
            }
            return !c.excludedFromIndex && !c.deleted;
        });
        if (props?.sortByPageOrder) {
            mergedChildren = mergedChildren.sort((a, b) => {
                const aIndex = childPagesOrder.indexOf(a.id);
                const bIndex = childPagesOrder.indexOf(b.id);
                return aIndex - bIndex;
            });
        }
        if (props) {
            const { filter, sort } = props;
            if (filter) {
                mergedChildren = mergedChildren.filter(filter);
            }
            if (sort) {
                mergedChildren = mergedChildren.sort(sort);
            }
        }
        return mergedChildren;
    }, [staticChildren, dynamicChildren, props]);
    return {
        childPages,
        withJaenPage: (childId, children) => {
            const jaenPage = staticChildren?.find(c => c.id === childId);
            return (_jsx(PageProvider, { jaenPage: { ...jaenPage, id: childId }, children: children }, childId));
        }
    };
};
